import { useCallback, useMemo } from "react";
import { formatCreatedDate } from "../components/UsersListTable";
import { getTeamUsersEmptyDescription } from "../components/OrgTeamMembersPanel";
import {
  getAllProviderUsersByProviderId,
  getAllSponsorUserList,
} from "../api/api-methods";
import {
  CONTACT_CATEGORY,
  getCategoryBadgeValue,
} from "../utils/requestContactDesignations";
import {
  getPortalAccessLabelFromSponsor,
  getPortalAccessLabelFromVendor,
} from "../utils/roles";
import { buildUserTableFilterDefinitions } from "../utils/teamUsersTable";
import {
  TEAM_MEMBER_EXPORT_HEADERS,
  buildTeamUsersExportData,
  withVendorContactExportHeaders,
} from "../utils/teamUsersExport";

export function useOrgTeamMembersSection({
  isVendorView,
  isSponsorView,
  currentUserType,
  vendorId,
  sponsorId,
  providerData,
  sponsorData,
  dualRoleOrg,
  catalogItems,
  teamUserFilters,
  hasTeamUserFilterChanges,
  debouncedTeamSearchTerm,
  teamUserSortOptions,
  navigate,
}) {
  const teamUserFilterDefinitions = useMemo(
    () =>
      buildUserTableFilterDefinitions({
        items: catalogItems,
        roleLabel: "Role",
        portalAccessLabel: "Portal Access",
        getPortalAccessValue: (row) =>
          isVendorView
            ? getPortalAccessLabelFromVendor(row, dualRoleOrg)
            : getPortalAccessLabelFromSponsor(row, dualRoleOrg),
        isVendorContext: isVendorView,
        getVqqContactValue: (row) => getCategoryBadgeValue(row, CONTACT_CATEGORY.RFI),
        getAssessmentContactValue: (row) =>
          getCategoryBadgeValue(row, CONTACT_CATEGORY.ASSESSMENT),
      }),
    [catalogItems, dualRoleOrg, isVendorView]
  );

  const getTeamUsersExportData = useCallback(
    () =>
      buildTeamUsersExportData({
        headers: withVendorContactExportHeaders(isVendorView, TEAM_MEMBER_EXPORT_HEADERS),
        fetchPage: (page) => {
          if (isVendorView) {
            return getAllProviderUsersByProviderId(
              vendorId,
              200,
              page,
              debouncedTeamSearchTerm,
              teamUserSortOptions,
              teamUserFilters
            );
          }
          return getAllSponsorUserList({
            currentPage: page,
            id: sponsorId,
            search_text: debouncedTeamSearchTerm,
            page_size: 200,
            sortOptions: teamUserSortOptions,
            filters: teamUserFilters,
          });
        },
        mapOptions: {
          getPortalAccessValue: (row) =>
            isVendorView
              ? getPortalAccessLabelFromVendor(row, dualRoleOrg)
              : getPortalAccessLabelFromSponsor(row, dualRoleOrg),
          includeVendorContacts: isVendorView,
          getVqqContactValue: (row) =>
            getCategoryBadgeValue(row, CONTACT_CATEGORY.RFI),
          getAssessmentContactValue: (row) =>
            getCategoryBadgeValue(row, CONTACT_CATEGORY.ASSESSMENT),
          formatCreated: formatCreatedDate,
        },
      }),
    [
      debouncedTeamSearchTerm,
      dualRoleOrg,
      isVendorView,
      sponsorId,
      teamUserFilters,
      teamUserSortOptions,
      vendorId,
    ]
  );

  const handleAddTeamMember = useCallback(() => {
    const sponsorOrgId =
      sponsorData?.hypercomply_org_id != null
        ? btoa(String(sponsorData.hypercomply_org_id))
        : "";
    const providerOrgId =
      providerData?.hc_org_id != null ? btoa(String(providerData.hc_org_id)) : "";
    const buildQs = (params) =>
      new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, v ?? ""]))
      ).toString();

    let url = "";
    if (currentUserType === "vendor") {
      url = `/Provider/addTeamMember?${buildQs({
        providerName: providerData?.name,
        providerId: providerData?.provider_id,
        orgId: providerOrgId,
      })}`;
    } else if (currentUserType === "sponsor") {
      url = `/Sponsor/AddTeamMember?${buildQs({
        id: sponsorId,
        name: sponsorData?.sponsor_name,
        orgId: sponsorOrgId,
      })}`;
    } else if (isVendorView) {
      url = `/Backend/Users/AddProviderUser?${buildQs({
        providerName: providerData?.name,
        providerId: providerData?.provider_id,
        orgId: providerOrgId,
      })}`;
    } else if (isSponsorView) {
      url = `/Backend/Users/Sponsors/AddUser?${buildQs({
        sponsorName: sponsorData?.sponsor_name,
        sponsorId,
      })}`;
    }
    if (url) navigate(url);
  }, [
    currentUserType,
    isSponsorView,
    isVendorView,
    navigate,
    providerData,
    sponsorData,
    sponsorId,
  ]);

  const emptyDescription = getTeamUsersEmptyDescription({
    hasFilterChanges: hasTeamUserFilterChanges,
    searchTerm: debouncedTeamSearchTerm,
  });

  return {
    teamUserFilterDefinitions,
    getTeamUsersExportData,
    handleAddTeamMember,
    emptyDescription,
  };
}
